import { DownloadIcon, RepeatIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Center,
    Divider,
    Flex,
    FormControl, FormLabel,
    Heading,
    HStack,
    Image,
    Input,
    Select,
    SimpleGrid,
    Spinner,
    Stack,
    Text,
    Th,
    Thead,
    VStack
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { PiPrinter } from "react-icons/pi";
import { alert, hasPermission, Logo } from '../../Utils';

import { Table, Tbody, Td, Tr } from '@chakra-ui/react';
import html2pdf from 'html2pdf.js';
import {
    FaCcAmex,
    FaCcDinersClub,
    FaCcDiscover,
    FaCcJcb,
    FaCcMastercard,
    FaCcVisa
} from 'react-icons/fa';
import InputMask from 'react-input-mask';
import { useSelector } from 'react-redux';
import { chargeCard } from '../../api';
import AccessDenied from '../../components/AccessDenied';
import PaymentOptions from './PaymentOptions';

const NewTransaction = () => {
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', phone: '', amount: '',
        address: '', city: '', state: '', zip: '', claimNumber: '',
        cardNumber: '', expirationDate: '', cvv: '', initials: '',
        agreeTerms: false
    });
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState("full");
    const [cardType, setCardType] = useState('');
    const [transactionId, setTransactionId] = useState('');


    const userInfo = useSelector((state) => state.user.info);

    const printRef = useRef();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (name === 'amount' && parseFloat(value) < 60) {
            setSelectedPlan('full');
        }

        if (name === 'cardNumber') {
            setCardType(getCardType(value));
            // console.log('Card input:', value, '| Detected type:', getCardType(value));

        }
    };

    const handleReset = () => {
        window.location.reload();
    };

    const handlePrint = () => {
        const element = printRef.current;

        const opt = {
            margin: 0.5,
            filename: 'invoice.pdf',
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        };

        html2pdf()
            .set(opt)
            .from(element)
            .toPdf()
            .get('pdf')
            .then((pdf) => {
                window.open(pdf.output('bloburl'), '_blank'); // opens in new tab with browser print
            });
    };


    const getPaymentDetails = () => {
        let today = new Date();
        let nextMonth = new Date(today);
        nextMonth.setMonth(today.getMonth() + 1);

        let twoMonthsLater = new Date(today);
        twoMonthsLater.setMonth(today.getMonth() + 2);

        let formatDate = (date) =>
            date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

        let amount = parseFloat(formData.amount) || 0;

        if (selectedPlan === "full") {
            return [{ amount: amount, dueDate: "Today" }];
        } else if (selectedPlan === "2-pay") {
            let firstPayment = Math.ceil(amount / 2);
            let secondPayment = amount - firstPayment;
            return [
                { amount: firstPayment, dueDate: "Today" },
                { amount: secondPayment, dueDate: formatDate(nextMonth) },
            ];
        } else {
            let installment = Math.floor(amount / 3);
            let firstPayment = amount - (installment * 2);
            return [
                { amount: firstPayment, dueDate: "Today" },
                { amount: installment, dueDate: formatDate(nextMonth) },
                { amount: installment, dueDate: formatDate(twoMonthsLater) },
            ];
        }
    };


    const getUserAgentAndIP = async () => {
        const userAgent = navigator.userAgent;

        // IP fetch with timeout
        const fetchIP = new Promise(async (resolve) => {
            try {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 3500);

                const res = await fetch('https://api.ipify.org?format=json', {
                    signal: controller.signal
                });
                clearTimeout(timeout);

                const data = await res.json();
                resolve({ ipAddress: data.ip, userAgent });
            } catch (err) {
                resolve({ userAgent }); // fallback without IP
            }
        });

        return fetchIP;
    };


    const handleSubmit = async () => {
        if (!validateStepTwo()) return;

        setLoading(true);

        const clientInfo = await getUserAgentAndIP();

        const payload = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            submittedBy: userInfo.firstName + " " + userInfo.lastName,
            phoneNumber: formData.phone.replace(/\D/g, ''),
            amount: formData.amount,
            invoiceNumber: formData.claimNumber,
            address: formData.address,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            payment_plan: selectedPlan, // full, 2-pay, or 3-pay
            country: 'USA',
            cardNumber: formData.cardNumber.replace(/\D/g, ''),
            expirationDate: formData.expirationDate.replace(/\D/g, ''),
            cvv: formData.cvv,
            initials: formData.initials,
            ip_address: clientInfo.ipAddress || '',
            user_agent: clientInfo.userAgent || '',
        };

        try {
            const result = await chargeCard(payload);

            setLoading(false);

            if (result.success) {
                setTransactionId(result.transactionId);
                setSubmitted(true);
            } else {
                alert("Something went wrong", 'error', result.message || 'Payment failed. Please try again.', "Okay !");
            }
        } catch (error) {
            // console.error('Payment failed:', error);
            setLoading(false);
        }
    };


    const validateStepTwo = ({ silent = false } = {}) => {
        const { cardNumber, expirationDate, cvv, initials } = formData;
        const requiredFields = {
            firstName: 'First Name',
            lastName: 'Last Name',
            email: 'Email Address',
            phone: 'Phone Number',
            amount: 'Claim Amount',
            address: 'Billing Address',
            city: 'City',
            state: 'State',
            zip: 'Zip Code',
            claimNumber: 'Claim Number'
        };

        for (let field in requiredFields) {
            if (!formData[field] || formData[field].toString().trim() === '') {
                alert("Sorry !", 'warning', `Please fill out the "${requiredFields[field]}" field.`, "Okay !");
                return false;
            }
        }


        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert("Sorry !", 'warning', `Please enter a valid email address.`, "Okay !");

            return false;
        }

        if (isNaN(formData.amount) || isNaN(formData.claimNumber)) {
            alert("Sorry !", 'warning', 'Claim Number and Amount must be numbers.', "Okay !");

            return false;
        }


        const showAlert = (msg) => {
            if (!silent)
                alert("Sorry !", 'warning', msg, "Okay !");
        };

        if (!formData.agreeTerms) {
            showAlert('You must agree to the payment terms before submitting.');
            return false;
        }


        if (!initials || initials.trim().length === 0) {
            showAlert('Please enter your initials to confirm.');
            return false;
        }

        if (!cardNumber || cardNumber.replace(/\D/g, '').length < 13) {
            showAlert('Please enter a valid card number.');
            return false;
        }
        if (!expirationDate || !/^(0[1-9]|1[0-2])\/\d{2}$/.test(expirationDate)) {
            showAlert('Please enter expiration in MM/YY format.');
            return false;
        }


        const [expMonth, expYear] = expirationDate.split('/');
        const currentDate = new Date();
        const expDate = new Date(`20${expYear}`, Number(expMonth) - 1, 1);

        if (expDate < new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)) {
            showAlert('Card expiration date is invalid or expired.');
            return false;
        }

        const cardType = getCardType(cardNumber);
        const cvvLength = cvv.length;

        if (cardType === 'Amex' && cvvLength !== 4) {
            showAlert('American Express requires a 4-digit CVV.');
            return false;
        } else if (cardType !== 'Amex' && cvvLength !== 3) {
            showAlert(`${cardType} requires a 3-digit CVV.`);
            return false;
        }



        return true;
    };


    const getCardType = (number) => {
        const cleaned = number.replace(/\D/g, '');
        if (/^4/.test(cleaned)) return 'Visa';
        if (/^5[1-5]/.test(cleaned)) return 'MasterCard';
        if (/^3[47]/.test(cleaned)) return 'Amex';
        if (/^6(?:011|5)/.test(cleaned) || /^64[4-9]/.test(cleaned)) return 'Discover';
        if (/^35(2[89]|[3-8][0-9])/.test(cleaned)) return 'JCB';
        if (/^3(?:0[0-5]|[68])/.test(cleaned)) return 'Diners Club';
        if (/^62/.test(cleaned)) return 'UnionPay';
        // return 'Unknown';
    };


    const getCardIcon = (type) => {
        switch (type) {
            case 'Visa': return <FaCcVisa size={28} color="#1a1f71" />;
            case 'MasterCard': return <FaCcMastercard size={28} color="#eb001b" />;
            case 'Amex': return <FaCcAmex size={28} color="#2e77bc" />;
            case 'Discover': return <FaCcDiscover size={28} color="#f76f00" />;
            case 'JCB': return <FaCcJcb size={28} color="#007bc1" />;
            case 'Diners Club': return <FaCcDinersClub size={28} color="#0066b3" />;
            case 'UnionPay': return <Text fontSize="sm" color="gray.500">UnionPay</Text>; // No native icon in `react-icons`
            default: return null;
        }
    };


    const handleDownloadPDF = () => {
        const element = printRef.current;

        const opt = {
            margin: 0.5,
            filename: `invoice_${formData.claimNumber || 'payment'}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
        };

        html2pdf().set(opt).from(element).save();
    };


    const paymentDetails = getPaymentDetails();
    const firstCharge = paymentDetails.length > 0 ? paymentDetails[0].amount : 0;


    const handleSubmitFake = async () => {
        setLoading(true);

        const clientInfo = await getUserAgentAndIP();

        const payload = {
            firstName: 'Miloaks',
            lastName: 'moal',
            email: 'su@examsple.com',
            submittedBy: 'Administrator',
            phoneNumber: '1234567890',
            amount: 176,
            claimNumber: 'INV1231111111',
            address: '123 Test St',
            city: 'Los Angeles',
            state: 'CA',
            zip: '90001',
            country: 'USA',
            cardNumber: '4111111111111111',
            expirationDate: '1226',
            cvv: '123',
            payment_plan: '3-pay',
            initials: 'JD',
            ip_address: clientInfo.ipAddress || '',
            user_agent: clientInfo.userAgent || '',
        };

        try {
            const result = await chargeCard(payload);

            setLoading(false);

            if (result.success) {
                setTransactionId(result.transactionId);
                alert("SUBMITTED")
                setSubmitted(true);
            } else {
                alert("Something went wrong", 'error', result.message || 'Payment failed. Please try again.', "Okay !");
            }
        } catch (error) {
            setLoading(false);
        }
    };




    if (!hasPermission('makeTransaction')) {
        return <AccessDenied msg={"You don't have permission to view this page"} />
    }

    return (
        <Box maxW="8xl" mx="auto" p="8">
            {/* Header Section */}
            <Box p={4} textAlign="center" style={{ borderBottom: "9px solid #f4b31b" }} mb={9}>
                <Image src={Logo} alt="Company Logo" mx="auto" mb={4} w={"10%"} borderRadius="md" />
                <Divider />
                <Heading mt={5} color={"gray.400"}>Take a Payment</Heading>

                <Text mt={5} color={"gray.900"}>This transaction will be logged as you are the one who tock it !</Text>
                <Text color={"gray.900"}>If this is not your name, Logout and log back in using your email and password</Text>
                <Text mt={5} color={"gray.900"} fontWeight={'bold'}>{userInfo.firstName + " " + userInfo.lastName}</Text>
                <Button onClick={handleSubmitFake}>Run Fake Charge</Button>

            </Box>

            {loading && (
                <Center my={10}><Spinner size="xl" color="blue.500" /></Center>
            )}

            {!loading && !submitted && step === 1 && (
                <>

                    <Box mb={10}>
                        <Box bg="blue.900" p={7} borderRadius="10px 10px 0 0" width="full" mb={5}><Center><Heading color={'#f4b31b'} size="lg">Claim Details</Heading></Center></Box>
                        <VStack spacing={4} mb={5} align="stretch">
                            <FormControl>
                                <FormLabel>Claim / Account Number </FormLabel>
                                <Input placeholder="Claim Number" name="claimNumber" value={formData.claimNumber} onChange={handleChange} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Claim Amount ($)</FormLabel>
                                <Input
                                    type="number"
                                    inputMode="decimal"
                                    placeholder="Enter Amount"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleChange}
                                />
                            </FormControl>
                            {formData.amount && formData.amount >= 20 && (<PaymentOptions selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} totalAmount={formData.amount} />)}
                        </VStack>
                    </Box>

                    <Box bg="blue.900" p={7} borderRadius="10px 10px 0 0" width="full" mb={5}><Center><Heading color={'#f4b31b'} size="lg">Billing Informations</Heading></Center></Box>
                    <VStack spacing={4} mb={5} align="stretch">
                        <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
                            <FormControl><FormLabel>First Name</FormLabel><Input placeholder="Your First Name" name="firstName" value={formData.firstName} onChange={handleChange} /></FormControl>
                            <FormControl><FormLabel>Last Name</FormLabel><Input placeholder="Your Last Name" name="lastName" value={formData.lastName} onChange={handleChange} /></FormControl>
                        </Stack>
                        <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
                            <FormControl flex={{ base: '1 1 100%', md: '1 1 40%' }}><FormLabel>Address</FormLabel><Input placeholder="Billing Address" name="address" value={formData.address} onChange={handleChange} /></FormControl>
                            <FormControl flex={{ base: '1 1 100%', md: '1 1 20%' }}><FormLabel>City</FormLabel><Input placeholder="Billing City" name="city" value={formData.city} onChange={handleChange} /></FormControl>
                            <FormControl flex={{ base: '1 1 100%', md: '1 1 10%' }}>

                                <FormControl>
                                    <FormLabel>State</FormLabel>
                                    <Select name="state" value={formData.state} onChange={handleChange} placeholder="Select State">
                                        <option value="AL">Alabama</option>
                                        <option value="AK">Alaska</option>
                                        <option value="AZ">Arizona</option>
                                        <option value="AR">Arkansas</option>
                                        <option value="CA">California</option>
                                        <option value="CO">Colorado</option>
                                        <option value="CT">Connecticut</option>
                                        <option value="DE">Delaware</option>
                                        <option value="FL">Florida</option>
                                        <option value="GA">Georgia</option>
                                        <option value="HI">Hawaii</option>
                                        <option value="ID">Idaho</option>
                                        <option value="IL">Illinois</option>
                                        <option value="IN">Indiana</option>
                                        <option value="IA">Iowa</option>
                                        <option value="KS">Kansas</option>
                                        <option value="KY">Kentucky</option>
                                        <option value="LA">Louisiana</option>
                                        <option value="ME">Maine</option>
                                        <option value="MD">Maryland</option>
                                        <option value="MA">Massachusetts</option>
                                        <option value="MI">Michigan</option>
                                        <option value="MN">Minnesota</option>
                                        <option value="MS">Mississippi</option>
                                        <option value="MO">Missouri</option>
                                        <option value="MT">Montana</option>
                                        <option value="NE">Nebraska</option>
                                        <option value="NV">Nevada</option>
                                        <option value="NH">New Hampshire</option>
                                        <option value="NJ">New Jersey</option>
                                        <option value="NM">New Mexico</option>
                                        <option value="NY">New York</option>
                                        <option value="NC">North Carolina</option>
                                        <option value="ND">North Dakota</option>
                                        <option value="OH">Ohio</option>
                                        <option value="OK">Oklahoma</option>
                                        <option value="OR">Oregon</option>
                                        <option value="PA">Pennsylvania</option>
                                        <option value="RI">Rhode Island</option>
                                        <option value="SC">South Carolina</option>
                                        <option value="SD">South Dakota</option>
                                        <option value="TN">Tennessee</option>
                                        <option value="TX">Texas</option>
                                        <option value="UT">Utah</option>
                                        <option value="VT">Vermont</option>
                                        <option value="VA">Virginia</option>
                                        <option value="WA">Washington</option>
                                        <option value="WV">West Virginia</option>
                                        <option value="WI">Wisconsin</option>
                                        <option value="WY">Wyoming</option>
                                    </Select>
                                </FormControl>

                            </FormControl>
                            <FormControl flex={{ base: '1 1 100%', md: '1 1 10%' }}><FormLabel>Zip Code</FormLabel><Input placeholder="Zip Code" name="zip" value={formData.zip} onChange={handleChange} /></FormControl>
                        </Stack>
                        <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
                            <FormControl>
                                <FormLabel>Phone Number</FormLabel>
                                <InputMask
                                    mask="(999) 999-9999"
                                    value={formData.phone}
                                    onChange={handleChange}
                                >
                                    {(inputProps) => (
                                        <Input {...inputProps} name="phone" placeholder="(123) 456-7890" type="tel"
                                            inputMode="tel" />
                                    )}
                                </InputMask>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Email </FormLabel>
                                <Input placeholder="Email Address" name="email" value={formData.email} onChange={handleChange} />
                                <small style={{ color: '#184e7a', fontSize: 12, marginLeft: 5 }}>This payment receipt will be sent to this email.</small>
                            </FormControl>
                        </Stack>
                    </VStack>


                    <Box bg="blue.900" p={7} borderRadius="10px 10px 0 0" width="full" mb={5}>
                        <Center>
                            <Heading color={'#f4b31b'} size="lg">Payment Agreement</Heading>
                        </Center>
                        <Center>
                            <Heading color={'red'} mt={3} size="lg">Please read this to the client before processing the payment over the phone.</Heading>
                        </Center>
                    </Box>
                    <Box bg="gray.50" p={5} borderRadius="md" mb={5}>



                        <Center>
                            <Box
                                p={4}
                                borderWidth="2px"
                                borderRadius="md"
                                borderColor="gray.100"
                                bg="gray.200"
                                // color="#f4b31b"
                                mb={6}
                                textAlign="center"
                                w={'90%'}
                            >
                                <HStack justify="center" spacing={2} mb={2}>
                                    <Text fontWeight="bold">
                                        {selectedPlan === "full"
                                            ? "Pay in Full"
                                            : selectedPlan === "2-pay"
                                                ? "2 Payments"
                                                : "3 Payments"}
                                    </Text>
                                </HStack>

                                <VStack align="center" fontSize="sm">
                                    {getPaymentDetails().map((payment, index) => (
                                        <Text key={index}>
                                            {index === 0 ? "First Charge" : `Next Charge #${index + 1}`}:
                                            <b> ${Number(payment.amount).toFixed(2)}</b> ({payment.dueDate})
                                        </Text>
                                    ))}
                                </VStack>
                            </Box>

                        </Center>

                        {selectedPlan === 'full' ?

                            <>
                                <Text mb={3}>
                                    I hereby authorize (Diagnostic Laboratory Science "DLS") to charge my provided payment method for the full amount of my claim/account as indicated and confirmed during this call.

                                </Text>
                                <Text mb={3}>
                                    I acknowledge that this charge represents a one-time payment for the Claim/Account, and I confirm that I have reviewed and approved the total amount prior to this authorization.
                                </Text>

                                <Text>
                                    By proceeding, I agree that the payment is final and understand that any disputes or concerns regarding this transaction must be addressed with (Diagnostic Laboratory Science "DLS") Billing Department at <b>(888-882-3330)</b> or using email <b>(BILLING@MYDLSLAB.COM)</b>.
                                </Text>

                            </>

                            :

                            <>
                                <Text mb={3}>
                                    I hereby acknowledge and agree that I will be charged according to the terms of my selected payment plan until my total balance is fully paid. {"\n\n"}
                                </Text>
                                <Text mb={3}>
                                    I understand that any <b>failure to complete the scheduled payments as agreed may result in the application of an additional fee of 12%</b> of the outstanding balance.
                                </Text>
                                <Text mb={8}>
                                    Furthermore, I authorize (Diagnostic Laboratory Science "DLS") to charge the payment method provided on the dates outlined in my payment plan, with a permissible variation of one day before or one day after each scheduled date, for the corresponding amount specified in each installment.
                                </Text>


                            </>
                        }


                        <FormControl isRequired mt={10} mb={3} bg={'gray.200'} p={3} style={{ borderRadius: 10 }}>
                            <HStack align="start" mb={5} mt={5}>
                                <input
                                    type="checkbox"
                                    id="agreeTerms"
                                    checked={formData.agreeTerms || false}
                                    onChange={(e) =>
                                        setFormData({ ...formData, agreeTerms: e.target.checked })
                                    }
                                    style={{ marginTop: '5px', transform: 'scale(1.3)' }} // 👈 makes checkbox bigger
                                />
                                <FormLabel htmlFor="agreeTerms" fontSize="md" fontWeight="normal" >
                                    By checking this box, the client has agreed to the terms listed above.
                                </FormLabel>
                            </HStack>

                            <FormLabel>Type Client Initials to Confirm</FormLabel>
                            <Input placeholder="e.g. J.D." bg={'gray.300'} name="initials" value={formData.initials} onChange={handleChange} />
                        </FormControl>


                    </Box>

                    <Box bg="blue.900" p={7} borderRadius="10px 10px 0 0" width="full" mb={5}><Center><Heading color={'#f4b31b'} size="lg">Payment Details</Heading></Center></Box>
                    <VStack spacing={4} mb={10} align="stretch">


                        <FormControl>
                            <FormLabel>
                                <HStack spacing={2}>
                                    <Text>Card Number</Text>
                                    {cardType && (
                                        <>
                                            {getCardIcon(cardType)}
                                            <Text fontSize="sm" color="gray.500">({cardType})</Text>
                                        </>
                                    )}
                                </HStack>
                            </FormLabel>

                            <InputMask
                                mask={cardType === 'Amex' ? '9999 999999 99999' : '9999 9999 9999 9999'}
                                value={formData.cardNumber}
                                onChange={handleChange}
                            >
                                {(inputProps) => (
                                    <Input {...inputProps} name="cardNumber" placeholder="1234 5678 9012 3456" />
                                )}
                            </InputMask>
                        </FormControl>



                        <HStack spacing={4}>
                            <FormControl>
                                <FormLabel>Expiration Date (MM/YY)</FormLabel>
                                <InputMask
                                    mask="99/99"
                                    value={formData.expirationDate}
                                    onChange={handleChange}
                                    placeholder="MM/YY"
                                >
                                    {(inputProps) => (
                                        <Input {...inputProps} name="expirationDate" />
                                    )}
                                </InputMask>
                            </FormControl>
                            <FormControl>
                                <FormLabel>
                                    <HStack spacing={2}>
                                        <Text>CVV</Text>
                                        {cardType === 'Amex' ? (
                                            <Text fontSize="sm" color="gray.500">(4 digits)</Text>
                                        ) : (
                                            cardType && <Text fontSize="sm" color="gray.500">(3 digits)</Text>
                                        )}
                                    </HStack>
                                </FormLabel>
                                <Input placeholder="CVV" name="cvv" value={formData.cvv} onChange={handleChange} />
                            </FormControl>
                        </HStack>
                    </VStack>

                    <HStack spacing={4} mt={9} style={{ marginTop: 90 }}>
                        <Button
                            bg="blue.900"
                            color="#f4b31b"
                            size="lg"
                            h="70px"
                            w="50%"
                            onClick={handleSubmit}
                        >
                            Submit Payment
                        </Button>
                    </HStack>
                </>
            )
            }

            {
                submitted && (
                    <Box textAlign="center" mt={10}>
                        <Heading size="lg" color="green.500">✅ Payment Submitted Successfully!</Heading>
                        <Text mt={4}>Thank you, {formData.firstName}. A confirmation has been sent to {formData.email}.</Text>


                        {/* Print & Make Another Payment Buttons */}
                        <HStack spacing={4} justify="center" mt={10} wrap="wrap">
                            <Button
                                leftIcon={<DownloadIcon />}
                                bg="gray.100"
                                color="blue.900"
                                variant="solid"
                                size="md"
                                onClick={handleDownloadPDF}
                            >
                                Download PDF
                            </Button>

                            <Button
                                leftIcon={<PiPrinter />}
                                bg="gray.100"
                                color="blue.900"
                                variant="solid"
                                size="md"
                                onClick={handlePrint}
                            >
                                Print Invoice
                            </Button>

                            <Button
                                leftIcon={<RepeatIcon />}
                                bg="gray.100"
                                color="blue.900"
                                variant="solid"
                                size="md"
                                onClick={handleReset}
                            >
                                Make Another Payment
                            </Button>
                        </HStack>


                        <Box ref={printRef} mt={10} textAlign="left" bg="white" p={6} borderRadius="md" border="1px solid gray">
                            {/* Invoice Header */}
                            <Box mb={6}>
                                <Flex
                                    direction={{ base: "column", md: "row" }}
                                    justify="space-between"
                                    align="center"
                                    wrap="wrap"
                                    gap={6}
                                >
                                    {/* Left: Logo + Business Info */}
                                    <HStack align="flex-start" spacing={4}>
                                        <Image src={Logo} alt="Company Logo" w="70px" borderRadius="md" />
                                        <Box>
                                            <Heading size="sm" mb={1}>Diagnostic Laboratory Science</Heading>
                                            <Text fontSize="sm">7711 Garden Grove Blvd</Text>
                                            <Text fontSize="sm">Garden Grove, CA 92841</Text>
                                            <Text fontSize="sm">Phone: 888-882-3330</Text>
                                        </Box>
                                    </HStack>

                                    {/* Center: Invoice Title */}
                                    <Box align="flex-end" >
                                        <Heading size="md" color="blue.900">Payment Invoice</Heading>
                                        <Text size="md" color="blue.900">Confirmation #: {transactionId}</Text>
                                    </Box>
                                </Flex>

                                <Divider mt={4} />
                            </Box>




                            {/* <Divider my={4} /> */}

                            <Heading size="sm" color={'blue.900'} mb={4}>Client Information</Heading>
                            <Box p={4} bg="gray.50" borderRadius="md">
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacingY={3} spacingX={10}>
                                    <HStack align="start">
                                        <Text fontWeight="bold" w="120px" fontSize="sm">Name:</Text>
                                        <Text fontSize="sm">{formData.firstName} {formData.lastName}</Text>
                                    </HStack>

                                    <HStack align="start">
                                        <Text fontWeight="bold" w="120px" fontSize="sm">Phone:</Text>
                                        <Text fontSize="sm">{formData.phone}</Text>
                                    </HStack>

                                    <HStack align="start">
                                        <Text fontWeight="bold" w="120px" fontSize="sm">Email:</Text>
                                        <Text fontSize="sm">{formData.email}</Text>
                                    </HStack>

                                    <HStack align="start">
                                        <Text fontWeight="bold" w="120px" fontSize="sm">Claim #:</Text>
                                        <Text fontSize="sm">{formData.claimNumber}</Text>
                                    </HStack>

                                    <HStack align="start" colSpan={2}>
                                        <Text fontWeight="bold" w="120px" fontSize="sm">Address:</Text>
                                        <Text fontSize="sm">
                                            {formData.address}, {formData.city}, {formData.state} {formData.zip}
                                        </Text>
                                    </HStack>
                                </SimpleGrid>
                            </Box>



                            <Divider my={4} />

                            {/* Payment Breakdown */}
                            <Heading size="sm" color={'blue.900'} mb={4}>Payment Details</Heading>

                            <Box p={4} bg="gray.50" borderRadius="md">
                                <Table variant="simple" size="sm">
                                    <Tbody>
                                        <Tr>
                                            <Td fontWeight="bold">Invoice Date</Td>
                                            <Td>{new Date().toLocaleDateString()}</Td>
                                        </Tr>
                                        <Tr>
                                            <Td fontWeight="bold">Total Amount</Td>
                                            <Td>${Number(formData.amount).toFixed(2)}</Td>
                                        </Tr>
                                        <Tr>
                                            <Td fontWeight="bold">Payment Method</Td>
                                            <Td>Credit Card (**** **** **** {formData.cardNumber.slice(-4)})</Td>
                                        </Tr>
                                        <Tr>
                                            <Td fontWeight="bold">Initial Charge</Td>
                                            <Td>${Number(firstCharge).toFixed(2)} (Paid Today)</Td>
                                        </Tr>
                                        <Tr>
                                            <Td fontWeight="bold">Payment Type</Td>
                                            <Td>
                                                {selectedPlan === '2-pay'
                                                    ? '2 Payments'
                                                    : selectedPlan === '3-pay'
                                                        ? '3 Payments'
                                                        : 'Payment in Full'}
                                            </Td>
                                        </Tr>

                                        {selectedPlan !== 'full' && paymentDetails.length > 1 && (
                                            <>
                                                {/* Remaining Balance Row */}
                                                <Tr>
                                                    <Td fontWeight="bold" color="red.600" fontSize="sm">Remaining Balance</Td>
                                                    <Td color="red.600" fontSize="sm">
                                                        ${(Number(formData.amount) - Number(firstCharge)).toFixed(2)}
                                                    </Td>
                                                </Tr>

                                                {/* Upcoming Payments Row */}
                                                <Tr>
                                                    <Td colSpan={2} pt={4}>
                                                        <Heading size="xs" mb={2}>Upcoming Payments</Heading>
                                                        <Table size="sm" variant="simple">
                                                            <Thead>
                                                                <Tr>
                                                                    <Th fontSize="sm">#</Th>
                                                                    <Th fontSize="sm">Amount</Th>
                                                                    <Th fontSize="sm">Due Date</Th>
                                                                </Tr>
                                                            </Thead>
                                                            <Tbody>
                                                                {paymentDetails.slice(1).map((payment, index) => (
                                                                    <Tr key={index}>
                                                                        <Td fontSize="sm">{index + 2}</Td>
                                                                        <Td fontSize="sm">${Number(payment.amount).toFixed(2)}</Td>
                                                                        <Td fontSize="sm">{payment.dueDate}</Td>
                                                                    </Tr>
                                                                ))}
                                                            </Tbody>
                                                        </Table>
                                                    </Td>
                                                </Tr>
                                            </>
                                        )}

                                    </Tbody>
                                </Table>
                            </Box>

                            {selectedPlan !== 'full' && paymentDetails.length > 1 &&

                                <Text fontSize="xs" color="gray.600" mt={3} fontStyle="italic">
                                    <b>Note:</b> If any scheduled payment is missed or delayed, additional interest and fees may apply in accordance with our payment terms.
                                </Text>


                            }

                            <Divider my={4} />

                            {/* Invoice Footer */}
                            <Text fontSize="sm" textAlign="center" color="gray.600">
                                Thank you for your payment. If you have any questions, contact us at <b>BILLING@MYDLSLAB.COM</b> or Call <b>888-882-3330</b>.
                            </Text>
                        </Box>


                    </Box>
                )
            }

        </Box >
    );
};

export default NewTransaction;
